// @ts-nocheck
import React, { useContext, useEffect, useState } from 'react';
import { firebaseContext } from './FirebaseProvider';
import { Scalars } from '../__generated__/pge-types';
import { onValue, ref } from 'firebase/database';

interface ContactDataIntercept {
  isEnabled?: boolean;
  startDate?: Scalars['LocalDate'];
  endDate?: Scalars['LocalDate'];
}

interface FeatureFlags {
  isPlannedDownTime: boolean;
  startStopEnabled: boolean;
  moveEnabled: boolean;
  /** Waiting has ended — by snapshot, timeout, or error. Values may be defaults. */
  loading: boolean;
  /** A snapshot actually arrived, so flag values are real rather than defaults. */
  flagsResolved: boolean;
  isTPAEnabled: boolean;
  isOutageByAddressEnabled: boolean;
  isClosedAccountsEnabled?: boolean;
  isPowerPortfolioEnabled?: boolean;
  isClosedAccountsEnabled?: boolean;
  contactDataIntercept?: ContactDataIntercept;
  displayMyAccountInfoBanner: boolean;
  isPGEPlusEVEnabled: boolean;
  isPGEPlusInstallerSignInEnabled: boolean;
  isClientProgramPromoModalEnabled: boolean;
  isSecureSiteModalEnabled: boolean;
  isPGEPlusRebateOnlyEnabled: boolean;
  isPspsActivated: boolean;
  isShowAccountSummaryMessage: boolean;
  isPGEPlusConsultationEnabled: boolean;
  isOutageMode: boolean;
  isShowOutageModeModal: boolean;
  OMSdowntime: boolean;
  webTurnOnTrustedDevice: boolean;
  isOutagesPSPSZonesEnabledOnLoad: boolean;
  isOutagesPSPSEnabled: boolean;
  isNestedOutageEnabled: boolean;
  isEnableApplePay: boolean;
  useLegacyApigeeEndpoint: boolean;
  useEdgeWidgetToken: boolean;
  useEdgeBillApiToken: boolean;
  isEnergyConsultationSiteVisitEnabled: boolean;
}

const initialState: FeatureFlags = {
  // runtime: firebase flags
  isPlannedDownTime: false,
  startStopEnabled: false,
  moveEnabled: false,
  loading: true,
  flagsResolved: false,
  isTPAEnabled: false,
  isOutageByAddressEnabled: false,
  isClosedAccountsEnabled: false,
  isPowerPortfolioEnabled: false,
  isClosedAccountsEnabled: false,
  displayMyAccountInfoBanner: false,
  isPGEPlusEVEnabled: false,
  isPGEPlusInstallerSignInEnabled: false,
  isClientProgramPromoModalEnabled: false,
  isSecureSiteModalEnabled: false,
  isPGEPlusRebateOnlyEnabled: false,
  isPspsActivated: false,
  isShowAccountSummaryMessage: false,
  isPGEPlusConsultationEnabled: false,
  isOutageMode: true,
  isShowOutageModeModal: false,
  OMSdowntime: false,
  webTurnOnTrustedDevice: false,
  isOutagesPSPSZonesEnabledOnLoad: false,
  isOutagesPSPSEnabled: false,
  isNestedOutageEnabled: false,
  isEnableApplePay: false,
  useLegacyApigeeEndpoint: false,
  useEdgeWidgetToken: false,
  useEdgeBillApiToken: false,
  isEnergyConsultationSiteVisitEnabled: false,
};

/* How long to wait for Firebase before rendering on the defaults above.
 * Chosen to stay under the point where a customer reads the spinner as broken. */
const FLAG_LOAD_TIMEOUT_MS = 5000;

const featureFlagsContext = React.createContext<FeatureFlags>(initialState);
const { Provider } = featureFlagsContext;

const FeatureFlagsProvider = (props: any) => {
  const { database } = useContext(firebaseContext);
  const [value, setValue] = useState(initialState);

  useEffect(() => {
    /* Important: Using firebase real-time database listner.
     * onValue() is called every time data is changed on featureFlags database.
     *
     * `loading` is not cosmetic. `useAccountCustomer` passes it straight through
     * as `skip` on `getAccountInfo`, the dashboard's root query, so any path that
     * leaves it `true` shows the customer a spinner forever and fires no GraphQL
     * at all. That is not hypothetical: in WAT's cross-site sandboxed iframe the
     * Realtime Database connection never completed, the snapshot never arrived,
     * and the dashboard hung silently with nothing logged. So every failure here
     * clears `loading` and proceeds on the defaults in `initialState`.
     *
     * After a timeout the listener is still attached, so a late snapshot still
     * updates the flags. The error path is terminal: Firebase cancels the listen
     * when it calls the error callback, and no later snapshot arrives.
     *
     * `loading: false` therefore does not mean the values are real. Anything that
     * acts on a flag being *off* (redirects to the legacy portal or away from a
     * page) must also require `flagsResolved`, which only a snapshot sets.
     */
    const proceedWithDefaults = (reason: string) => {
      console.error(`Feature flags unavailable, using defaults: ${reason}`);
      setValue(current =>
        current.loading ? { ...current, loading: false } : current,
      );
    };

    if (!database) {
      proceedWithDefaults('Firebase database is unavailable');
      return;
    }

    const timeout = setTimeout(
      () => proceedWithDefaults(`no snapshot within ${FLAG_LOAD_TIMEOUT_MS}ms`),
      FLAG_LOAD_TIMEOUT_MS,
    );

    const unsubscribe = onValue(
      ref(database, '/featureFlags'),
      snapshot => {
        clearTimeout(timeout);
        if (!snapshot.exists()) {
          proceedWithDefaults('the /featureFlags node is empty');
          return;
        }
        setValue(current => ({
          ...current,
          isPlannedDownTime: !!snapshot.val()?.isPlannedDownTime,
          startStopEnabled: !!snapshot.val()?.startStopEnabled,
          moveEnabled: !!snapshot.val()?.moveEnabled,
          isTPAEnabled: !!snapshot.val()?.isTPAEnabled,
          isOutageByAddressEnabled: !!snapshot.val()?.isOutageByAddressEnabled,
          isPowerPortfolioEnabled: !!snapshot.val()?.isPowerPortfolioEnabled,
          isClosedAccountsEnabled: !!snapshot.val()?.isClosedAccountsEnabled,
          contactDataIntercept: snapshot.val()?.contactDataIntercept,
          displayMyAccountInfoBanner: !!snapshot.val()
            ?.displayMyAccountInfoBanner,
          isPGEPlusEVEnabled: !!snapshot.val()?.isPGEPlusEVEnabled,
          isPGEPlusInstallerSignInEnabled: !!snapshot.val()
            ?.isPGEPlusInstallerSignInEnabled,
          isClientProgramPromoModalEnabled: !!snapshot.val()
            ?.isClientProgramPromoModalEnabled,
          isSecureSiteModalEnabled: !!snapshot.val()?.isSecureSiteModalEnabled,
          isPGEPlusRebateOnlyEnabled: !!snapshot.val()
            ?.isPGEPlusRebateOnlyEnabled,
          isPspsActivated: !!snapshot.val()?.isPspsActivated,
          isShowAccountSummaryMessage: !!snapshot.val()
            ?.isShowAccountSummaryMessage,
          isPGEPlusConsultationEnabled: !!snapshot.val()
            ?.isPGEPlusConsultationEnabled,
          isOutageMode: !!snapshot.val()?.isOutageMode,
          isShowOutageModeModal: !!snapshot.val()?.isShowOutageModeModal,
          OMSdowntime: !!snapshot.val()?.OMSdowntime,
          webTurnOnTrustedDevice: !!snapshot.val()?.webTurnOnTrustedDevice,
          isOutagesPSPSZonesEnabledOnLoad: !!snapshot.val()
            ?.isOutagesPSPSZonesEnabledOnLoad,
          isOutagesPSPSEnabled: !!snapshot.val()?.isOutagesPSPSEnabled,
          isNestedOutageEnabled: !!snapshot.val()?.isNestedOutageEnabled,
          isEnableApplePay: !!snapshot.val()?.isEnableApplePay,
          useLegacyApigeeEndpoint: !!snapshot.val()?.useLegacyApigeeEndpoint,
          useEdgeWidgetToken: !!snapshot.val()?.useEdgeWidgetToken,
          useEdgeBillApiToken: !!snapshot.val()?.useEdgeBillApiToken,
          isEnergyConsultationSiteVisitEnabled: !!snapshot.val()
            ?.isEnergyConsultationSiteVisitEnabled,
          loading: false,
          flagsResolved: true,
        }));
      },
      error => {
        clearTimeout(timeout);
        proceedWithDefaults(error.message);
      },
    );

    return () => {
      clearTimeout(timeout);
      // Guarded because `jest.mock('firebase/database')` automocks return
      // undefined here, and a throw in cleanup fails unrelated suites.
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [database]);

  return <Provider value={value}>{props.children}</Provider>;
};

export { FeatureFlagsProvider, featureFlagsContext, ContactDataIntercept };
